import React, { useState } from 'react';
import '../../styles/Page.css';
import '../../styles/Hero.css';
import './LeadFinder.css';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import LeadFinderHero from './LeadFinderHero';
import HowTo from './HowTo';
import ResultsList from './ResultsList';
import LegalNote from '../../components/LegalNote';

function LeadFinder() {
  const [subredditUrl, setSubredditUrl] = useState('');
  const [criteria, setCriteria] = useState('');
  const [numberOfPosts, setNumberOfPosts] = useState(20);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const { loading: searchLoading, error: searchError, request: searchApi, clearError: clearSearchError } = useApi();
  const { fetchQuota } = useAuth();


  // State for expanded rows and generation
  const [expandedPermalink, setExpandedPermalink] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [generatedResults, setGeneratedResults] = useState({});
  const { loading: generationLoading, request: generationApi } = useApi();

  const getSubredditName = (input) => {
    const trimmedInput = input.trim();
    const match = trimmedInput.match(/reddit\.com\/r\/([^/]+)/);
    if (match) return match[1];
    if (trimmedInput.startsWith('r/')) return trimmedInput.substring(2);
    return trimmedInput;
  };

  const handleFocus = (event) => event.target.select();

  const handleSearch = async (e) => {
    e.preventDefault();
    setResults([]);
    setHasSearched(true);
    try {
      const data = await searchApi('/api/find_leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subreddit_url: subredditUrl,
          number_of_posts: numberOfPosts,
          criteria: criteria
        })
      });
      console.log('LeadFinder - Received search results:', data);
      setResults(data);
      fetchQuota();
    } catch (err) {
      console.error("Search failed:", err);
    }
  };

  const handleRowClick = (permalink) => {
    setExpandedPermalink(prev => (prev === permalink ? null : permalink));
  };

  const handleDraftChange = (permalink, text) => {
    setDrafts(prev => ({ ...prev, [permalink]: text }));
  };

  const handleGenerate = async (item, type) => {
    const draftText = drafts[item.permalink];
    if (!draftText) {
      return;
    }

    setGeneratedResults(prev => ({
      ...prev,
      [item.permalink]: { loading: true, results: [] }
    }));

    const endpoint = type === 'dm' ? '/api/generate_dm' : '/api/generate_comment';
    const body = type === 'dm'
      ? {
        username: item.username,
        context_text: item.text,
        template: draftText
      }
      : {
        username: item.username,
        context_text: item.text,
        user_text: draftText
      };

    try {
      const data = await generationApi(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      console.log('LeadFinder - Received generation results:', data);
      const resultTexts = data.dm_texts || data.comment_texts;

      setGeneratedResults(prev => ({
        ...prev,
        [item.permalink]: { type, results: resultTexts, loading: false }
      }));

    } catch (err) {
      // Notification removed
      setGeneratedResults(prev => ({
        ...prev,
        [item.permalink]: { results: [], loading: false }
      }));
    }
  };

  const handleClearResults = () => {
    setResults([]);
    setHasSearched(false);
    clearSearchError();
    setExpandedPermalink(null);
    setDrafts({});
    setGeneratedResults({});
    setNumberOfPosts(15);
  };

  const extractToJson = () => {
    if (results.length === 0) return;
    const data = {
      metadata: {
        subreddit_url: getSubredditName(subredditUrl),
        number_of_posts_searched: numberOfPosts,
        search_criteria: criteria,
        result_count: results.length,
      },
      data: results.map(r => ({
        ...r,
        draft: drafts[r.permalink] || '',
        generated_dms: generatedResults[r.permalink]?.type === 'dm' ? generatedResults[r.permalink]?.results : [],
        generated_comments: generatedResults[r.permalink]?.type === 'comment' ? generatedResults[r.permalink]?.results : [],
      }))
    };
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reddit_leads_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);
  };

  return (
    <>
      <title>Lead Finder - WizeHelper</title>
      <meta name="description" content="Find potential customers on Reddit by searching posts and comments based on your criteria." />

      <main className="mx-auto px-6 py-8" style={{ maxWidth: '88rem' }}>
        <LeadFinderHero
          subredditUrl={subredditUrl}
          setSubredditUrl={setSubredditUrl}
          criteria={criteria}
          setCriteria={setCriteria}
          numberOfPosts={numberOfPosts}
          setNumberOfPosts={setNumberOfPosts}
          handleSearch={handleSearch}
          searchLoading={searchLoading}
          handleFocus={handleFocus}
        />
        <LegalNote />
        {searchError && (
          <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
            {searchError.status === 403 || searchError.status === 429 ? (
              searchError.message
            ) : (
              <><span className="font-medium">Error!</span> {searchError.message}</>
            )}
          </div>
        )}
        {searchLoading ? (
          <div className="text-center my-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {results.length > 0 ? (
              <ResultsList
                results={results}
                extractToJson={extractToJson}
                handleClearResults={handleClearResults}
                expandedPermalink={expandedPermalink}
                handleRowClick={handleRowClick}
                drafts={drafts}
                handleDraftChange={handleDraftChange}
                generatedResults={generatedResults}
                handleGenerate={handleGenerate}
                generationLoading={generationLoading}
              />
            ) : hasSearched ? (
              <div className="text-center my-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-8" data-testid="leadFinder-noLeadsFound">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white">No Leads Found</h3>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                  We couldn't find any leads matching your criteria. Try adjusting your search terms.
                </p>
              </div>
            ) : (
              <div className="my-12">
                <HowTo />
              </div>
            )}
          </>
        )}
      </main>
    </>
  );
}

export default LeadFinder;